import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { activationReportService, pickParams } from '../services/activationReportService';
import { ReportFilters, SummaryOptions } from '../types';

// Giữ dữ liệu cũ khi đổi bộ lọc/trang để bảng không nháy về skeleton
const KEY = 'activationReport';

// Kích hoạt xảy ra ở app người dùng nên không có mutation nào làm mới cache này:
// ghi đè mặc định của App (10 phút, không refetch khi mount) để số liệu không cũ quá 1 phút.
// Lỗi 4xx mang tính xác định (vd. 422 q quá rộng) nên không thử lại.
const REPORT_QUERY = {
  staleTime: 60_000,
  refetchOnMount: true,
  retry: (count: number, error: any) => !(error?.status >= 400 && error?.status < 500) && count < 1,
} as const;

// queryKey chỉ chứa tham số endpoint đọc (pickParams, cùng bộ với request):
// đổi q/type/trang ở tab khác không làm tổng quan tải lại hay giữ thêm bản cache trùng.
export const useOverview = (f: ReportFilters) =>
  useQuery({
    queryKey: [KEY, 'overview', pickParams('overview', f)],
    queryFn: () => activationReportService.overview(f),
    ...REPORT_QUERY,
    placeholderData: keepPreviousData,
  });

export const useByTarget = (f: ReportFilters) =>
  useQuery({
    queryKey: [KEY, 'byTarget', pickParams('by-target', f)],
    queryFn: () => activationReportService.byTarget(f),
    ...REPORT_QUERY,
    placeholderData: keepPreviousData,
  });

export const useByBatch = (f: ReportFilters) =>
  useQuery({
    queryKey: [KEY, 'byBatch', pickParams('by-batch', f)],
    queryFn: () => activationReportService.byBatch(f),
    ...REPORT_QUERY,
    placeholderData: keepPreviousData,
  });

// `enabled`: danh sách mã đã dùng chỉ tải khi người dùng mở ra
export const useUsedCodes = (f: ReportFilters, enabled = true) =>
  useQuery({
    queryKey: [KEY, 'usedCodes', pickParams('used-codes', f)],
    queryFn: () => activationReportService.usedCodes(f),
    enabled,
    ...REPORT_QUERY,
    placeholderData: keepPreviousData,
  });

export const useByUser = (f: ReportFilters) =>
  useQuery({
    queryKey: [KEY, 'byUser', pickParams('by-user', f)],
    queryFn: () => activationReportService.byUser(f),
    ...REPORT_QUERY,
    placeholderData: keepPreviousData,
  });

export const useFilterOptions = () =>
  useQuery({
    queryKey: [KEY, 'filterOptions'],
    queryFn: () => activationReportService.filterOptions(),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

// Lớp phụ thuộc trường: chưa chọn trường thì không gọi API.
// Không keepPreviousData: đổi S1->S2 mà còn lớp S1 thì có thể chọn nhầm lớp của S1.
export const useClassOptions = (schoolId?: string) =>
  useQuery({
    queryKey: [KEY, 'classOptions', schoolId],
    queryFn: () => activationReportService.classOptions(schoolId as string),
    enabled: !!schoolId,
    staleTime: 5 * 60 * 1000,
  });

// Cùng khoá cache với thanh lọc nên không gọi thêm API; chỉ để ghi tên thay id vào file xuất
export const useSummaryOptions = (schoolId?: string): SummaryOptions => {
  const { data: options } = useFilterOptions();
  const { data: classes } = useClassOptions(schoolId);
  return { ...options, classes };
};
